import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, Phone, Mail, MapPin } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Reveal } from "../components/Reveal";
import { CTAButton } from "../components/CTAButton";
import { submitContact } from "../lib/api";
import { CONTACT } from "../data";

export default function Contact() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", property_type: "", location: "", tenancy_status: "", message: "",
  });
  const [loading, setLoading] = useState(false);
  const setV = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please add your name, email, and mobile number.");
      return;
    }
    setLoading(true);
    try {
      await submitContact(form);
      toast.success("Inquiry sent. We'll follow up within one business day.");
      setForm({ name: "", email: "", phone: "", property_type: "", location: "", tenancy_status: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* Hero */}
      <section className="pt-40 md:pt-48 pb-16" data-testid="contact-hero">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="kept-label brass-text mb-8">
            Contact Us
          </motion.div>
          <h1
            className="font-serif text-4xl md:text-5xl lg:text-6xl font-light tracking-tighter leading-[1.12] text-kept-ink max-w-3xl overflow-hidden"
            style={{ paddingBottom: "0.12em", marginBottom: "-0.12em" }}
          >
            <motion.span className="block" initial={{ y: "110%" }} animate={{ y: "0%" }} transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.15 }}>
              Ready to protect your investment?
            </motion.span>
          </h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.8 }} className="text-muted-foreground mt-8 max-w-2xl text-base md:text-lg leading-relaxed">
            Request a consultation and discover what premium property management looks like,
            responsive, thorough, and designed around your goals.
          </motion.p>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-28 md:pb-36 grid lg:grid-cols-12 gap-px bg-kept-ink/10 border border-kept-ink/10" data-testid="contact-split">
        {/* Primary: appraisal booking */}
        <div className="lg:col-span-5 bg-kept-ink grain p-10 md:p-14 flex flex-col" data-testid="contact-appraisal-panel">
          <div className="kept-label text-kept-brass mb-6">The one step that matters</div>
          <h2 className="font-serif text-3xl md:text-4xl font-light text-kept-sand leading-tight">
            Book your free rental appraisal.
          </h2>
          <p className="text-kept-sand/60 mt-6 leading-relaxed">
            The fastest way to start. Tell us about your property and we'll arrange a call to
            walk you through what premium management delivers, no obligation.
          </p>
          <div className="mt-10">
            <CTAButton light testId="contact-book-appraisal" />
          </div>

          <div className="mt-auto pt-14 space-y-5">
            <a href={`tel:${CONTACT.phone.replace(/\s/g, "")}`} className="flex items-center gap-4 text-kept-sand/80 hover:text-kept-brass transition-colors" data-testid="contact-phone">
              <Phone size={16} className="text-kept-brass" /> {CONTACT.phone}
            </a>
            <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-4 text-kept-sand/80 hover:text-kept-brass transition-colors break-all" data-testid="contact-email">
              <Mail size={16} className="text-kept-brass" /> {CONTACT.email}
            </a>
            <div className="flex items-center gap-4 text-kept-sand/60">
              <MapPin size={16} className="text-kept-brass" /> {CONTACT.location}
            </div>
          </div>
        </div>

        {/* Secondary: general enquiry */}
        <div className="lg:col-span-7 bg-kept-sand p-10 md:p-14" data-testid="contact-form-panel">
          <Reveal>
            <div className="kept-label brass-text mb-6">General enquiry</div>
            <form onSubmit={submit} className="space-y-8" data-testid="contact-form">
              <div className="grid md:grid-cols-2 gap-8">
                <input className="kept-input" placeholder="Your name" value={form.name} onChange={(e) => setV("name")(e.target.value)} data-testid="contact-name" />
                <input className="kept-input" placeholder="Email address" type="email" value={form.email} onChange={(e) => setV("email")(e.target.value)} data-testid="contact-email-input" />
                <input className="kept-input" placeholder="Mobile number" value={form.phone} onChange={(e) => setV("phone")(e.target.value)} data-testid="contact-phone-input" />
                <Select value={form.property_type} onValueChange={setV("property_type")}>
                  <SelectTrigger className="kept-input h-auto" data-testid="contact-property-type"><SelectValue placeholder="Property type" /></SelectTrigger>
                  <SelectContent className="rounded-none">
                    <SelectItem value="Residential">Residential</SelectItem>
                    <SelectItem value="Commercial">Commercial</SelectItem>
                    <SelectItem value="Mixed-use">Mixed-use</SelectItem>
                  </SelectContent>
                </Select>
                <input className="kept-input" placeholder="Location (city / suburb)" value={form.location} onChange={(e) => setV("location")(e.target.value)} data-testid="contact-location" />
              </div>
              <Select value={form.tenancy_status} onValueChange={setV("tenancy_status")}>
                <SelectTrigger className="kept-input h-auto" data-testid="contact-tenancy"><SelectValue placeholder="Current tenancy status" /></SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="Currently tenanted">Currently tenanted</SelectItem>
                  <SelectItem value="Vacant">Vacant</SelectItem>
                  <SelectItem value="Not sure">Not sure</SelectItem>
                </SelectContent>
              </Select>
              <textarea className="kept-input resize-none" rows={3} placeholder="Your goals" value={form.message} onChange={(e) => setV("message")(e.target.value)} data-testid="contact-message" />
              <motion.button
                type="submit"
                disabled={loading}
                whileTap={{ scale: 0.98 }}
                className="group inline-flex items-center gap-3 bg-kept-ink text-kept-brass px-8 py-4 kept-label disabled:opacity-60"
                data-testid="contact-submit"
              >
                {loading ? "Sending…" : "Send Inquiry"}
                <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
              </motion.button>
              <p className="text-xs text-muted-foreground">No obligations. We'll follow up within one business day.</p>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
